import React, { FC, ReactNode } from "react";
import {
  LuAlarmClock,
  LuAlertTriangle,
  LuAtSign,
  LuCheckCircle2,
  LuClipboardList,
  LuMessageSquare,
  LuPenLine,
  LuUpload,
  LuUserPlus,
  LuZap,
} from "react-icons/lu";

import { NotificationCategory, NotificationItem } from "./notifications.types";
import { notificationSentence } from "./notifications.utils";

/** Icon + colors per kind; the same everywhere (page, bell, Home). */
const KIND: Record<
  NotificationCategory,
  { icon: ReactNode; color: string; bg: string }
> = {
  mentions: { icon: <LuAtSign />, color: "#6B4EFF", bg: "#F0EDFF" },
  comments: { icon: <LuMessageSquare />, color: "#2F6FED", bg: "#EAF1FE" },
  assigned: { icon: <LuUserPlus />, color: "#0E8A7E", bg: "#E3F5F2" },
  subtasks: { icon: <LuCheckCircle2 />, color: "#2E9E62", bg: "#E9F7EF" },
  reminders: { icon: <LuAlarmClock />, color: "#B7791F", bg: "#FDF3E1" },
  automations: { icon: <LuZap />, color: "#5B6678", bg: "#EEF0F4" },
  client_uploads: { icon: <LuUpload />, color: "#D9480F", bg: "#FFEDE3" },
  signatures: { icon: <LuPenLine />, color: "#7C3AED", bg: "#F0EDFF" },
  client_alerts: { icon: <LuAlertTriangle />, color: "#C2410C", bg: "#FFF1E6" },
  client_requests: {
    icon: <LuClipboardList />,
    color: "#0E7490",
    bg: "#E6F6FA",
  },
};

export const KindIcon: FC<{
  category: NotificationCategory;
  size?: "sm" | "md";
}> = ({ category, size = "md" }) => {
  const kind = KIND[category] ?? KIND.automations;
  return (
    <span
      aria-hidden={"true"}
      className={`flex flex-none items-center justify-center rounded-[10px] ${
        size === "sm" ? "h-8 w-8 text-[15px]" : "h-9 w-9 text-[17px]"
      }`}
      style={{ color: kind.color, background: kind.bg }}>
      {kind.icon}
    </span>
  );
};

/** Just the kind's icon in its color (the page's category list). */
export const KindGlyph: FC<{
  category: NotificationCategory;
  colored?: boolean;
}> = ({ category, colored }) => {
  const kind = KIND[category] ?? KIND.automations;
  return (
    <span
      aria-hidden={"true"}
      style={colored ? { color: kind.color } : undefined}>
      {kind.icon}
    </span>
  );
};

/** "Priya Shah assigned you to 2025 T1" with the names in bold. */
export const Sentence: FC<{ item: NotificationItem; className?: string }> = ({
  item,
  className,
}) => {
  const { who, what } = notificationSentence(item);
  return (
    <span className={className}>
      {who && <span className={"font-bold text-[#1F2433]"}>{who} </span>}
      {what.map((segment, i) => (
        <span
          key={i}
          className={
            segment.bold
              ? "font-bold text-[#1F2433]"
              : "font-medium text-[#4A5163]"
          }>
          {segment.text}
        </span>
      ))}
    </span>
  );
};

export const UnreadDot: FC<{ show: boolean }> = ({ show }) => (
  <span
    aria-label={show ? "Unread" : undefined}
    className={`h-2 w-2 flex-none rounded-full ${
      show ? "bg-[#6B4EFF]" : "bg-transparent"
    }`}
  />
);

export const CountPill: FC<{ count?: number; active?: boolean }> = ({
  count,
  active,
}) =>
  count ? (
    <span
      className={`min-w-[22px] rounded-full px-[7px] py-[1px] text-center text-[11.5px] font-extrabold tabular-nums ${
        active ? "bg-white text-[#6B4EFF]" : "bg-[#F0EDFF] text-[#6B4EFF]"
      }`}>
      {count > 99 ? "99+" : count}
    </span>
  ) : null;
